import { useMemo } from 'react'
import { useSetting } from './useSetting'
import { useTransactions } from './useTransactions'
import { useAppSymbols } from './useAppSymbols'
import { aggregateDailyPL, localDateKey } from '../lib/pl'
import { useStartOfDayEquity } from './useStartOfDayEquity'

export const MAX_LOSS_KEY = 'max-loss-per-day'

export type MaxLossType = 'dollar' | 'percent'

export interface MaxLossSettings {
  type: MaxLossType
  value: number | null
  enabled: boolean
}

export interface MaxLossLimitStatus {
  /** Today's realized P/L (signed; ≤ 0 means a loss). */
  dailyLoss: number
  /** Today's P/L as percent of start-of-day account equity, or null when unknown. */
  dailyPct: number | null
  /** The selected limit type (dollar or percent). */
  type: MaxLossType | null
  /** The selected limit value. */
  value: number | null
  /** True when enabled and the limit is exceeded. */
  capReached: boolean
  /** Short reason for banners/tooltips. */
  capReason: string | null
}

function parseSettings(raw: unknown): MaxLossSettings {
  if (typeof raw === 'object' && raw != null && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>
    const type = (obj.type === 'dollar' || obj.type === 'percent') ? obj.type : 'dollar'
    const value = typeof obj.value === 'number' && obj.value > 0 ? obj.value : null
    return {
      type,
      value: value ?? null,
      enabled: obj.enabled === true,
    }
  }
  return { type: 'dollar', value: null, enabled: false }
}

export function useMaxLossLimit(): MaxLossLimitStatus {
  const [raw] = useSetting<unknown>(MAX_LOSS_KEY, null)
  const settings = useMemo(() => parseSettings(raw), [raw])
  const txQuery = useTransactions()
  const { appSymbols } = useAppSymbols()
  const startEquity = useStartOfDayEquity()

  const dailyLoss = useMemo(() => {
    const daily = aggregateDailyPL(txQuery.data ?? [], { symbolFilter: appSymbols })
    const todayKey = localDateKey(new Date().toISOString())
    const day = daily.get(todayKey)
    return day ? day.pl : 0
  }, [txQuery.data, appSymbols])

  const dailyPct =
    startEquity != null && startEquity > 0 ? (dailyLoss / startEquity) * 100 : null

  const capReached =
    settings.enabled &&
    settings.value != null &&
    settings.value > 0 &&
    (settings.type === 'percent'
      ? dailyPct != null && dailyPct <= -settings.value
      : dailyLoss <= -settings.value)

  const capReason = capReached
    ? settings.type === 'percent'
      ? `Max daily loss reached (${Math.abs(dailyPct ?? 0).toFixed(2)}% / ${settings.value}%)`
      : `Max daily loss reached ($${Math.abs(dailyLoss).toFixed(0)} / $${settings.value})`
    : null

  return {
    dailyLoss,
    dailyPct,
    type: settings.enabled && settings.value != null ? settings.type : null,
    value: settings.enabled && settings.value != null ? settings.value : null,
    capReached,
    capReason,
  }
}
